import httpClient from './httpClient';
import { parseApiError, retryOperation } from '../utils/errorHandler';

const getSettlements = async (page = 1, pageSize = 10, filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    ...(filters.settlement_id && { settlement_id: filters.settlement_id }),
    ...(filters.supplier_id && { supplier_id: filters.supplier_id }),
    ...(filters.supplier_name && { supplier_name: filters.supplier_name }),
    ...(filters.status && { status: filters.status }),
    ...(filters.approval_status && { approval_status: filters.approval_status }),
    ...(filters.payment_status && { payment_status: filters.payment_status }),
    ...(filters.settlement_type && { settlement_type: filters.settlement_type }),
    ...(filters.start_date && { start_date: filters.start_date }),
    ...(filters.end_date && { end_date: filters.end_date }),
    ...(filters.sort_by && { sort_by: filters.sort_by }),
    ...(filters.sort_order && { sort_order: filters.sort_order }),
  };

  try {
    const response = await httpClient.get('/api/settlements', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching settlements:', error);
    throw error;
  }
};

const getSettlement = async (settlementId, cacheBust = null) => {
  try {
    const params = cacheBust ? { _t: cacheBust } : {};
    const response = await httpClient.get(`/api/settlements/${settlementId}`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching settlement:', error);
    throw error;
  }
};

const createSettlement = async (settlementData) => {
  try {
    const response = await retryOperation(async () => {
      return await httpClient.post('/api/settlements', settlementData);
    }, 2);
    
    return response.data;
  } catch (error) {
    const parsedError = parseApiError(error);
    console.error('Error creating settlement:', parsedError);
    throw parsedError;
  }
};

const updateSettlement = async (settlementId, settlementData) => {
  try {
    const response = await retryOperation(async () => {
      return await httpClient.put(`/api/settlements/${settlementId}`, settlementData);
    }, 2);
    
    return response.data;
  } catch (error) {
    const parsedError = parseApiError(error);
    console.error('Error updating settlement:', parsedError);
    throw parsedError;
  }
};

const deleteSettlement = async (settlementId) => {
  try {
    const response = await httpClient.delete(`/api/settlements/${settlementId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting settlement:', error);
    throw error;
  }
};

const submitForApproval = async (settlementId) => {
  try {
    const response = await httpClient.post(`/api/settlements/${settlementId}/submit`, {});
    return response.data;
  } catch (error) {
    console.error('Error submitting settlement for approval:', error);
    throw error;
  }
};

const approveSettlement = async (settlementId) => {
  try {
    const response = await httpClient.post(`/api/settlements/${settlementId}/approve`, {});
    return response.data;
  } catch (error) {
    console.error('Error approving settlement:', error);
    throw error;
  }
};

const rejectSettlement = async (settlementId, reason = '') => {
  try {
    const response = await httpClient.post(`/api/settlements/${settlementId}/reject`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting settlement:', error);
    throw error;
  }
};

const markAsPaid = async (settlementId) => {
  try {
    const response = await httpClient.post(`/api/settlements/${settlementId}/mark-as-paid`, {});
    return response.data;
  } catch (error) {
    console.error('Error marking settlement as paid:', error);
    throw error;
  }
};

const requestRefund = async (settlementId, reason = '') => {
  try {
    const response = await httpClient.post(`/api/settlements/${settlementId}/request-refund`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error requesting refund:', error);
    throw error;
  }
};

const processRefund = async (settlementId) => {
  try {
    const response = await httpClient.post(`/api/settlements/${settlementId}/process-refund`, {});
    return response.data;
  } catch (error) {
    console.error('Error processing refund:', error);
    throw error;
  }
};

const uploadAttachment = async (settlementId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await httpClient.post(
      `/api/settlements/${settlementId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading attachment:', error);
    throw error;
  }
};

const deleteAttachment = async (settlementId, fileId) => {
  try {
    const response = await httpClient.delete(
      `/api/settlements/${settlementId}/attachments/${fileId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting attachment:', error);
    throw error;
  }
};

const getAttachments = async (settlementId) => {
  try {
    const response = await httpClient.get(`/api/settlements/${settlementId}/attachments`);
    return response.data;
  } catch (error) {
    console.error('Error fetching attachments:', error);
    throw error;
  }
};

const extractFromFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await httpClient.post(
      '/api/settlements/extract',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error extracting data from file:', error);
    throw error;
  }
};

const getAvailablePOs = async (filters = {}) => {
  const params = {
    ...(filters.supplier_id && { supplier_id: filters.supplier_id }),
    ...(filters.status && { status: filters.status }),
  };

  try {
    const response = await httpClient.get('/api/settlements/available-pos', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching available purchase orders:', error);
    throw error;
  }
};

const getAvailableSCs = async (filters = {}) => {
  const params = {
    ...(filters.supplier_id && { supplier_id: filters.supplier_id }),
    ...(filters.client_ref && { client_ref: filters.client_ref }),
  };

  try {
    const response = await httpClient.get('/api/settlements/available-scs', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching available subcontract orders:', error);
    throw error;
  }
};

const parseObjectIdData = (data) => {
  if (!data) return data;
  
  if (Array.isArray(data)) {
    return data.map(parseObjectIdData);
  }
  
  if (typeof data === 'object') {
    const parsed = {};
    for (const [key, value] of Object.entries(data)) {
      if (key === '_id' && typeof value === 'object' && value.$oid) {
        parsed[key] = value.$oid;
      } else if (key.endsWith('_id') && typeof value === 'object' && value.$oid) {
        parsed[key] = value.$oid;
      } else if (key.endsWith('_at') && typeof value === 'object' && value.$date) {
        parsed[key] = new Date(value.$date);
      } else if (typeof value === 'object' && value !== null) {
        parsed[key] = parseObjectIdData(value);
      } else {
        parsed[key] = value;
      }
    }
    return parsed;
  }
  
  return data;
};

const wrappedGetSettlements = async (...args) => {
  const data = await getSettlements(...args);
  return parseObjectIdData(data);
};

const wrappedGetSettlement = async (...args) => {
  const data = await getSettlement(...args);
  return parseObjectIdData(data);
};

const wrappedGetAvailablePOs = async (...args) => {
  const data = await getAvailablePOs(...args);
  return parseObjectIdData(data);
};

const wrappedGetAvailableSCs = async (...args) => {
  const data = await getAvailableSCs(...args);
  return parseObjectIdData(data);
};

const withErrorHandling = (fn) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      const parsedError = parseApiError(error);
      console.error(`API Error in ${fn.name}:`, parsedError);
      throw parsedError;
    }
  };
};

const settlementService = {
  getSettlements: wrappedGetSettlements,
  getSettlement: wrappedGetSettlement,
  createSettlement,
  updateSettlement,
  deleteSettlement: withErrorHandling(deleteSettlement),
  submitForApproval: withErrorHandling(submitForApproval),
  approveSettlement: withErrorHandling(approveSettlement),
  rejectSettlement: withErrorHandling(rejectSettlement),
  markAsPaid: withErrorHandling(markAsPaid),
  requestRefund: withErrorHandling(requestRefund),
  processRefund: withErrorHandling(processRefund),
  uploadAttachment,
  deleteAttachment: withErrorHandling(deleteAttachment),
  getAttachments: withErrorHandling(getAttachments),
  extractFromFile,
  getAvailablePOs: wrappedGetAvailablePOs,
  getAvailableSCs: wrappedGetAvailableSCs,
  parseObjectIdData,
  withErrorHandling
};

export default settlementService;
